import api from 'services/api';
import { REJECT, array, nullable, num, object, str, type Received } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { readResponse } from 'api/shared/readResponse';

/**
 * A row of nap-backend `GET /python_libraries` (routes/python_libraries.js).
 *
 * The read is a BARE ARRAY: no `{ nodes, pagination, config, filtersConfig }` wrapper, and the
 * handler accepts no query parameters — it is a fixed `SELECT … ORDER BY name ASC`, after which
 * the route enriches each row with `folder_path` (buildFolderPath). Paging, sorting and
 * filtering are therefore entirely client-side; see the note on the slice's
 * `fetchPythonLibraries`.
 *
 * `content` is deliberately absent — only the single-GET selects it, which is what keeps this
 * list cheap. So `PythonLibraryRow` is a genuinely LIGHTER shape than `PythonLibrary`, not
 * equal-by-coincidence with it.
 *
 * The `documentation` column the SELECT also returns is deliberately not carried — see
 * `SavePythonLibrary`.
 *
 * `created_at` / `updated_at` are nullable because the columns are: migration 0200 declares them
 * `TIMESTAMP DEFAULT CURRENT_TIMESTAMP` and 0252 only retypes them to `TIMESTAMPTZ` — neither
 * adds `NOT NULL`. Nothing in the route guarantees a value either.
 */
export interface PythonLibraryRow {
  readonly python_library_id: number;
  readonly name: string;
  readonly filename: string;
  readonly description: string | null;
  readonly hash: string;
  readonly folder_id: number | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  // Enriched per row by the route (null when the library has no folder).
  readonly folder_path: FolderPath | null;
}

const rowSchema = object<PythonLibraryRow>({
  python_library_id: num(REJECT),
  name: str(),
  filename: str(),
  description: nullable(str()),
  hash: str(),
  folder_id: nullable(num()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
  folder_path: nullable(folderPathSchema),
});

// `REJECT` because the whole response IS the list: a body that is not an array
// is not a library-less installation, it is an answer we could not read, and
// substituting `[]` renders the page empty with nothing saying so. Same rule as
// `recurrences` and `fetchOccurrenceSeries` — where the list is the payload
// rather than a set of options, its absence is not a fact.
const listSchema = array(rowSchema, REJECT);

/**
 * GET `/python_libraries`. Returns every library the user may list, name-ascending.
 * Throws on transport error; a row whose identity (`python_library_id`) drifted is dropped
 * rather than shown unaddressable, and a body that is not an array reads as no libraries.
 *
 * The response has no wrapper object, so the schema is parsed at the empty root path and a
 * row's problems are reported under `[0].field`.
 */
export const getPythonLibraries = async (): Promise<Received<readonly PythonLibraryRow[]>> => {
  const res = await api.get('/python_libraries');
  return readResponse(res.data, { schema: listSchema, source: 'getPythonLibraries' });
};
